import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useBreadcrumbs } from "../context/BreadcrumbContext";
import { usePractice } from "../context/PracticeContext";
import {
  metricAlertsApi,
  type LookerFieldDef,
} from "../api/metricAlerts";
import { PageSkeleton } from "../components/PageSkeleton";
import { EmptyState } from "../components/EmptyState";
import { cn } from "../lib/utils";
import {
  Database,
  Search as SearchIcon,
  ChevronDown,
  ChevronRight,
} from "lucide-react";

function FieldGroup({ label, fields }: { label: string; fields: LookerFieldDef[] }) {
  if (fields.length === 0) return null;
  return (
    <div>
      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label} ({fields.length})
      </div>
      <div className="grid grid-cols-1 gap-x-4 gap-y-0.5 sm:grid-cols-2">
        {fields.map((f) => (
          <button
            key={f.name}
            type="button"
            onClick={() => navigator.clipboard?.writeText(f.name)}
            title="Click to copy field name"
            className="flex items-baseline gap-2 rounded px-1 py-0.5 text-left hover:bg-accent/40"
          >
            <code className="truncate font-mono text-[11px]">{f.name}</code>
            <span className="shrink-0 text-[10px] text-muted-foreground">{f.type}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function ExploreFields({
  companyId,
  model,
  explore,
}: {
  companyId: string;
  model: string;
  explore: string;
}) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["looker-explore-fields", companyId, model, explore],
    queryFn: () => metricAlertsApi.lookerExploreFields(companyId, model, explore),
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) {
    return <div className="px-4 py-3 text-xs text-muted-foreground">Loading fields…</div>;
  }
  if (error) {
    return (
      <div className="px-4 py-3 text-xs text-destructive">
        {error instanceof Error ? error.message : "Failed to load fields"}
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="space-y-3 bg-muted/20 px-4 py-3">
      <FieldGroup label="Dimensions" fields={data.dimensions} />
      <FieldGroup label="Measures" fields={data.measures} />
      {data.dimensions.length === 0 && data.measures.length === 0 && (
        <div className="text-xs text-muted-foreground">No fields returned for this explore.</div>
      )}
    </div>
  );
}

function ExploreRow({
  companyId,
  model,
  name,
  label,
}: {
  companyId: string;
  model: string;
  name: string;
  label: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-border last:border-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-4 py-2 text-left hover:bg-accent/40"
      >
        {open ? (
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        )}
        <code className="font-mono text-xs">{name}</code>
        {label && label !== name && (
          <span className="truncate text-xs text-muted-foreground">{label}</span>
        )}
      </button>
      {open && <ExploreFields companyId={companyId} model={model} explore={name} />}
    </div>
  );
}

export function AdminLookerSchema() {
  const { setBreadcrumbs } = useBreadcrumbs();
  const { selectedPracticeId } = usePractice();
  const [filter, setFilter] = useState("");

  useEffect(() => {
    setBreadcrumbs([{ label: "Administration" }, { label: "Looker Schema" }]);
  }, [setBreadcrumbs]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["looker-schema", selectedPracticeId],
    queryFn: () => metricAlertsApi.lookerSchema(selectedPracticeId!),
    enabled: !!selectedPracticeId,
  });

  const q = filter.trim().toLowerCase();
  const models = useMemo(() => {
    const all = data?.models ?? [];
    if (!q) return all;
    return all
      .map((m) => ({
        ...m,
        explores: m.explores.filter(
          (e) =>
            e.name.toLowerCase().includes(q) || (e.label ?? "").toLowerCase().includes(q),
        ),
      }))
      .filter((m) => m.explores.length > 0);
  }, [data, q]);

  const exploreCount = useMemo(
    () => (data?.models ?? []).reduce((n, m) => n + m.explores.length, 0),
    [data],
  );

  if (!selectedPracticeId) {
    return <EmptyState icon={Database} message="Select a practice to browse Looker schema." />;
  }

  return (
    <div className="mx-auto max-w-5xl space-y-4 p-6">
      <div>
        <h2 className="text-xl font-bold">Looker Schema</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          The live models, explores, and fields in this company's Looker instance — the ground
          truth for metric queries and monitor thresholds. Click any field to copy its name.
        </p>
      </div>

      {isLoading && <PageSkeleton variant="list" />}

      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {error instanceof Error ? error.message : "Failed to load Looker schema"}
        </div>
      )}

      {data && (
        <>
          <div className="flex items-center gap-2 rounded-md border border-input bg-background px-3 py-2">
            <SearchIcon className="h-3.5 w-3.5 text-muted-foreground" />
            <input
              type="text"
              placeholder="Filter explores…"
              className="flex-1 bg-transparent text-sm focus:outline-none"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
            <span className="text-xs text-muted-foreground">
              {data.models.length} models · {exploreCount} explores
            </span>
          </div>

          {models.length === 0 ? (
            <EmptyState icon={Database} message="No explores match that filter." />
          ) : (
            models.map((m) => (
              <div key={m.name} className="rounded-lg border border-border bg-card">
                <div className="flex items-center gap-2 border-b border-border px-4 py-2.5">
                  <Database className="h-3.5 w-3.5 text-muted-foreground" />
                  <code className="font-mono text-sm font-medium">{m.name}</code>
                  <span className="text-xs text-muted-foreground">
                    {m.explores.length} {m.explores.length === 1 ? "explore" : "explores"}
                  </span>
                </div>
                <div>
                  {m.explores.map((e) => (
                    <ExploreRow
                      key={e.name}
                      companyId={selectedPracticeId}
                      model={m.name}
                      name={e.name}
                      label={e.label}
                    />
                  ))}
                </div>
              </div>
            ))
          )}
        </>
      )}
    </div>
  );
}
